<script>
/* eslint-disable vue/no-unused-components */
import customDashboardsMixin from 'shared/mixins/customDashboardsMixin';
import { useAlert } from 'dashboard/composables';
import { createApp, h } from 'vue';

import {
  numberOfColumns,
  cellSpacing,
  cellAspectRatio,
  resizableHandles,
  allowFloating,
  defaultCellOptions,
  generateId,
} from '../../../../../../../shared/helpers/CustomDashboardsHelper';
import CellHeader from './CellHeader.vue';
import Spinner from 'shared/components/Spinner.vue';

import {
  DashboardLayoutComponent,
  PanelDirective,
  PanelsDirective,
} from '@syncfusion/ej2-vue-layouts';

import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { DATE_RANGE_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'CustomDashboards',
  components: {
    DashboardLayoutComponent,
    PanelDirective,
    PanelsDirective,
    CellHeader,
    Spinner,
  },
  mixins: [customDashboardsMixin],
  props: {
    metricsFromAPI: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateWidgetsList'],
  data() {
    return {
      numberOfColumns,
      cellSpacing,
      cellAspectRatio,
      resizableHandles,
      allowFloating,

      widgets: [],
      mountedApps: new Map(),
      alreadyProcessedDashboard: false,
    };
  },
  computed: {
    dashboardId() {
      return this.$route.params.dashboardId;
    },
  },
  watch: {
    widgets: {
      handler() {
        this.$emit('updateWidgetsList', this.widgets);
        this.$nextTick(() => {
          this.remountAllWidgets();
        });
      },
      deep: true,
    },
    dashboardId() {
      this.unmountAllApps();
      this.widgets = [];
      this.$nextTick(() => {
        this.addInitialCellsToDashboard();
      });
    },
  },
  mounted() {
    setTimeout(() => {
      this.alreadyProcessedDashboard = true;
      this.addInitialCellsToDashboard();
    }, 150);
  },
  created() {
    emitter.on(
      'updatingWidgetHeaderTextInCustomDashboard',
      this.updateHeaderContent
    );
  },
  beforeUnmount() {
    emitter.off(
      'updatingWidgetHeaderTextInCustomDashboard',
      this.updateHeaderContent
    );
    this.unmountAllApps();
  },
  methods: {
    addInitialCellsToDashboard() {
      this.metricsFromAPI.forEach((widgetOptions, index) => {
        setTimeout(() => {
          this.createNewWidget(widgetOptions);
        }, index * 50);
      });
    },

    getComponentForWidget(widget) {
      return this.widgetOptionsList.find(e => e.type === widget.type)
        ?.component;
    },

    buildHeaderHtml(id) {
      return `<div id="header-mount-${id}" class="header-mount-point"></div>`;
    },

    buildContentHtml(id) {
      return `<div id="content-mount-${id}" class="content-mount-point"></div>`;
    },

    createNewWidget(widgetOptions) {
      const {
        id = generateId(),
        sizeX = defaultCellOptions.sizeX,
        sizeY = defaultCellOptions.sizeY,
        row = defaultCellOptions.row,
        col = defaultCellOptions.col,
        contentProps = {
          display: this.displaysList[0],
          dateRange: this.defaultDateRange(),
        },
      } = widgetOptions;

      if (widgetOptions.type === 'conversations' && !contentProps.submetric)
        contentProps.submetric = this.conversationSubmetricsList[0];

      if (
        widgetOptions.type === 'conversationResolutionReason' &&
        (!contentProps.multipleSubmetrics || !contentProps.submetric)
      ) {
        contentProps.multipleSubmetrics = [
          this.conversationResolutionReasonsSubmetricsList[0],
        ];
        contentProps.submetric =
          this.conversationResolutionReasonsSubmetricsList[0];
      }

      if (
        widgetOptions.type === 'static' &&
        (!contentProps.display || contentProps.display.type === 'summary')
      )
        contentProps.display = this.displaysList.find(
          e => e.type === 'static_text'
        );

      const newWidget = {
        id,
        sizeX,
        sizeY,
        row,
        col,
        type: widgetOptions.type,
        headerText: widgetOptions.headerText || widgetOptions.header,
        contentProps,
        header: this.buildHeaderHtml(id),
        content: this.buildContentHtml(id),
      };

      this.widgets.push(newWidget);

      const isRenderingWidgetsFromApi = widgetOptions.id;
      const mountDelay = isRenderingWidgetsFromApi ? 300 : 0;

      setTimeout(() => {
        this.mountWidgetComponents(newWidget);
      }, mountDelay);

      if (!isRenderingWidgetsFromApi)
        useAlert(this.$t('CUSTOM_DASHBOARDS.ADD_WIDGET.SUCCESS_MESSAGE'));
    },
    remountAllWidgets() {
      this.unmountAllApps();
      this.widgets.forEach(widget => {
        this.mountWidgetComponents(widget);
      });
    },

    mountWidgetComponents(widget) {
      const maxAttempts = 10;
      let attempts = 0;

      const tryMount = () => {
        const headerEl = document.getElementById(`header-mount-${widget.id}`);
        const contentEl = document.getElementById(`content-mount-${widget.id}`);

        if (!headerEl && !contentEl && attempts < maxAttempts) {
          attempts += 1;
          setTimeout(tryMount, 100);
          return;
        }

        if (headerEl && !this.mountedApps.has(`header-${widget.id}`)) {
          const headerApp = createApp({
            render: () =>
              h(CellHeader, {
                text: widget.headerText,
                widgetId: widget.id,
                editMode: this.editMode,
              }),
          });
          this.copyAppContext(headerApp);
          headerApp.mount(headerEl);
          this.mountedApps.set(`header-${widget.id}`, headerApp);
        }

        const vueComponent = this.getComponentForWidget(widget);
        if (
          contentEl &&
          vueComponent &&
          !this.mountedApps.has(`content-${widget.id}`)
        ) {
          const contentApp = createApp({
            render: () =>
              h(vueComponent, {
                ...widget.contentProps,
                id: widget.id,
                sizeX: widget.sizeX,
                sizeY: widget.sizeY,
              }),
          });
          this.copyAppContext(contentApp);
          contentApp.mount(contentEl);
          this.mountedApps.set(`content-${widget.id}`, contentApp);
        }
      };

      tryMount();
    },

    copyAppContext(app) {
      const ctx = this.$root?.$.appContext;
      if (!ctx) return;

      // eslint-disable-next-line no-underscore-dangle
      const appContext = app._context;
      if (!appContext) return;

      appContext.components = { ...ctx.components };
      appContext.directives = { ...ctx.directives };
      appContext.provides = { ...ctx.provides };
      app.config.globalProperties = { ...ctx.config.globalProperties };
    },

    unmountWidget(widgetId) {
      const headerApp = this.mountedApps.get(`header-${widgetId}`);
      if (headerApp) {
        headerApp.unmount();
        this.mountedApps.delete(`header-${widgetId}`);
      }

      const contentApp = this.mountedApps.get(`content-${widgetId}`);
      if (contentApp) {
        contentApp.unmount();
        this.mountedApps.delete(`content-${widgetId}`);
      }
    },

    unmountAllApps() {
      this.mountedApps.forEach(app => {
        try {
          app.unmount();
        } catch (e) {
          // Ignore unmount errors
        }
      });
      this.mountedApps.clear();
    },

    updateWidget(widgetId, contentProps) {
      const index = this.widgets.findIndex(item => item.id === widgetId);
      if (index !== -1) {
        this.unmountWidget(widgetId);
        this.widgets[index].contentProps = {
          ...this.widgets[index].contentProps,
          ...contentProps,
        };
        this.$nextTick(() => {
          this.mountWidgetComponents(this.widgets[index]);
        });
      }
    },

    updateHeaderContent({ widgetId, newValue }) {
      const index = this.widgets.findIndex(item => item.id === widgetId);
      if (index !== -1) {
        this.unmountWidget(widgetId);
        this.widgets[index].headerText = newValue;
        this.$nextTick(() => {
          this.mountWidgetComponents(this.widgets[index]);
        });
      }
    },

    deleteWidget(widgetId) {
      this.unmountWidget(widgetId);
      const index = this.widgets.findIndex(item => item.id === widgetId);
      if (index !== -1) this.widgets.splice(index, 1);
      useAlert(this.$t('CUSTOM_DASHBOARDS.DELETE_WIDGET.SUCCESS_MESSAGE'));
    },

    onResizeStop() {
      this.syncCellProperties();
    },
    onDragStop() {
      this.syncCellProperties();
    },
    syncCellProperties() {
      const dashboardEl = document.getElementById('dashboard_layout');
      if (!dashboardEl?.ej2_instances?.[0]) return;

      const dashboardLayoutComponent = dashboardEl.ej2_instances[0];
      dashboardLayoutComponent.panels.forEach(panel => {
        const widgetIndex = this.widgets.findIndex(w => w.id === panel.id);
        if (widgetIndex !== -1) {
          this.widgets[widgetIndex].row = panel.row;
          this.widgets[widgetIndex].col = panel.col;
          this.widgets[widgetIndex].sizeX = panel.sizeX;
          this.widgets[widgetIndex].sizeY = panel.sizeY;
        }
      });
    },
    onResizeStart() {},
    onResize() {},

    defaultDateRange() {
      const translatedDateRangeOptions = Object.values(DATE_RANGE_OPTIONS).map(
        option => ({
          ...option,
          name: this.$t(option.translationKey),
        })
      );
      const { offset, id: rangeId } = translatedDateRangeOptions[0];
      const from = getUnixStartOfDay(subDays(new Date(), offset));
      const to = getUnixEndOfDay(new Date());
      const range = [from, to];
      return {
        type: rangeId,
        range,
      };
    },
  },
};
</script>

<template>
  <div :class="{ 'p-4': editMode, 'pt-4': !editMode }">
    <Spinner v-if="!alreadyProcessedDashboard" />
    <div v-else class="control-section dashboard-dynamic w-full">
      <DashboardLayoutComponent
        id="dashboard_layout"
        :columns="numberOfColumns"
        :cell-spacing="cellSpacing"
        :cell-aspect-ratio="cellAspectRatio"
        :resizable-handles="resizableHandles"
        :resize-start="onResizeStart"
        :resize="onResize"
        :resize-stop="onResizeStop"
        :drag-stop="onDragStop"
        :allow-floating="allowFloating"
        :allow-resizing="editMode"
        :allow-dragging="editMode"
      >
        <PanelsDirective>
          <PanelDirective
            v-for="widget in widgets"
            :id="widget.id"
            :key="widget.id"
            :size-x="widget.sizeX"
            :size-y="widget.sizeY"
            :row="widget.row"
            :col="widget.col"
            :header="widget.header"
            :content="widget.content"
            :max-size-x="numberOfColumns"
            :min-size-x="1"
            :max-size-y="3"
            :min-size-y="1"
          />
        </PanelsDirective>
      </DashboardLayoutComponent>
    </div>
  </div>
</template>

<style>
@import '@syncfusion/ej2-vue-layouts/styles/material.css';
@import '@syncfusion/ej2-base/styles/material.css';

#dashboard_layout .e-panel .e-panel-content {
  vertical-align: top;
  font-size: 14px;
  line-height: normal;
  overflow: auto;
  height: 100%;
}

#dashboard_layout .e-panel .e-panel-header {
  padding: 0;
}

#dashboard_layout .e-panel {
  transition: none !important;
}

#dashboard_layout {
  width: 100% !important;
}

.header-mount-point,
.content-mount-point {
  width: 100%;
  height: 100%;
}
</style>
